import React, { useState, useEffect } from 'react';
import { UserCheck, Trash2, PlusCircle, ChevronDown, Loader2, AlertCircle, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

export default function TeacherAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterClass, setFilterClass] = useState('');
  const [form, setForm] = useState({ teacher_id: '', class_id: '', subject_name: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [asgRes, tchRes, clsRes, sesRes] = await Promise.all([
        api.get('/academics/teacher-assignments'),
        api.get('/teachers?status=active&limit=100'),
        api.get('/academics/classes'),
        api.get('/academics/current-session'),
      ]);
      setAssignments(asgRes.data || []);
      setTeachers(tchRes.data || []);
      setClasses(clsRes.data || []);

      // Collect subjects from session or teachers
      const subjectSet = new Set();
      (tchRes.data || []).forEach(t => (t.subjects || []).forEach(s => subjectSet.add(s)));
      const sessionSubjects = (sesRes.data?.subjects || []).map(s => s.name || s);
      sessionSubjects.forEach(s => subjectSet.add(s));
      if (subjectSet.size === 0) {
        ['Mathematics', 'English Language', 'Basic Science', 'Social Studies', 'Civic Education',
         'Basic Technology', 'Computer Science', 'Home Economics', 'Agricultural Science',
         'Business Studies', 'Christian Religious Studies', 'Islamic Religious Studies',
         'Physical Education', 'French', 'Yoruba', 'Igbo', 'Hausa'].forEach(s => subjectSet.add(s));
      }
      setSubjects([...subjectSet].sort());
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!form.teacher_id || !form.class_id || !form.subject_name) {
      toast.error('All fields required'); return;
    }
    setSaving(true);
    try {
      const res = await api.post('/academics/teacher-assignments', form);
      toast.success('Assignment created');
      setAssignments(prev => [...prev, res.data]);
      setForm({ teacher_id: '', class_id: '', subject_name: '' });
      setShowForm(false);
    } catch (err) {
      toast.error(err?.detail || 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this assignment?')) return;
    setDeleting(id);
    try {
      await api.delete(`/academics/teacher-assignments/${id}`);
      toast.success('Assignment removed');
      setAssignments(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      toast.error(err?.detail || 'Failed to remove');
    } finally {
      setDeleting('');
    }
  };

  const filtered = filterClass
    ? assignments.filter(a => a.class_id === filterClass)
    : assignments;

  // Group by class
  const grouped = {};
  filtered.forEach(a => {
    const key = a.class_name || a.class_id;
    grouped[key] = grouped[key] || [];
    grouped[key].push(a);
  });

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="teacher-assignments-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Teacher Assignments</h1>
          <p className="text-sm text-slate-500 mt-0.5">Assign teachers to specific subjects per class</p>
        </div>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
          data-testid="add-assignment-btn">
          <PlusCircle className="w-4 h-4" /> Assign Teacher
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6" data-testid="assignment-form">
          <h3 className="font-semibold text-slate-800 mb-4 text-sm uppercase tracking-wide">New Assignment</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Teacher</label>
              <div className="relative">
                <select value={form.teacher_id} onChange={e => setForm(f => ({ ...f, teacher_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-indigo-400 appearance-none pr-8"
                  data-testid="assign-teacher-select">
                  <option value="">Select teacher...</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Class</label>
              <div className="relative">
                <select value={form.class_id} onChange={e => setForm(f => ({ ...f, class_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-indigo-400 appearance-none pr-8"
                  data-testid="assign-class-select">
                  <option value="">Select class...</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Subject</label>
              <div className="relative">
                <select value={form.subject_name} onChange={e => setForm(f => ({ ...f, subject_name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-indigo-400 appearance-none pr-8"
                  data-testid="assign-subject-select">
                  <option value="">Select subject...</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleAdd} disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2 rounded-lg disabled:opacity-50 transition-colors"
              data-testid="save-assignment-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><UserCheck className="w-4 h-4" /> Save Assignment</>}
            </button>
            <button onClick={() => setShowForm(false)} className="text-sm text-slate-500 hover:text-slate-700 px-3 py-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400" />
        <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white outline-none focus:border-indigo-400 appearance-none"
          data-testid="filter-class-select">
          <option value="">All classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="text-xs text-slate-400">{filtered.length} assignment{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Grouped Assignments */}
      {Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <UserCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-semibold text-slate-500">No assignments yet</p>
          <p className="text-sm text-slate-400 mt-1">Assign teachers to class-subject combos above</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([className, items]) => (
            <div key={className} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-indigo-500" />
                <span className="font-bold text-slate-700 text-sm">{className}</span>
                <span className="ml-auto text-xs text-slate-400">{items.length} teacher{items.length !== 1 ? 's' : ''} assigned</span>
              </div>
              <div className="divide-y divide-slate-100">
                {items.map(a => (
                  <div key={a.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-indigo-700">{a.teacher_name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-700">{a.teacher_name}</p>
                        <p className="text-xs text-slate-400">{a.subject_name}</p>
                      </div>
                    </div>
                    <button onClick={() => handleDelete(a.id)} disabled={deleting === a.id}
                      className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-red-50"
                      data-testid={`remove-assignment-${a.id}`}>
                      {deleting === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
